import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useSelector } from 'react-redux'
import { getSelectedCustomerId } from '../selectors/customers'
import prepareHeaders from '../utils/prepareHeaders'

const baseUrl = '/secure/policies/teams'
const TEAMS_APP_CATALOG_CACHE_SECONDS = 5 * 60

export const teamsApi = createApi({
  reducerPath: 'teamsPolicies',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTeamsPolicies: builder.query({
      query: ({ configType, customerId }) => ({
        url: `${baseUrl}/${configType}`,
        params: { customerId },
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
    }),
    getTeamsPolicy: builder.query({
      query: ({ configType, customerId, identity }) => ({
        url: `${baseUrl}/${configType}`,
        params: { customerId, identity },
      }),
    }),
    getTeamsAppCatalog: builder.query({
      query: ({ appIds = [], customerId }) => ({
        url: `${baseUrl}/app-catalog`,
        params: { customerId, ...(appIds.length ? { appIds: appIds.join(',') } : {}) },
      }),
      transformResponse: (response) => (Array.isArray(response) ? response : []),
      keepUnusedDataFor: TEAMS_APP_CATALOG_CACHE_SECONDS,
    }),
    getTeamsAppCatalogPages: builder.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
      },
      query: ({ pageParam, queryArg: { customerId, search = '' } }) => ({
        url: `${baseUrl}/app-catalog`,
        params: {
          customerId,
          pageSize: 100,
          ...(search ? { search } : {}),
          ...(pageParam ? { pageToken: pageParam } : {}),
        },
      }),
      transformResponse: (response) => ({
        apps: Array.isArray(response?.apps) ? response.apps : [],
        nextPageToken: response?.nextPageToken || null,
      }),
      keepUnusedDataFor: TEAMS_APP_CATALOG_CACHE_SECONDS,
    }),
    createTeamsPolicy: builder.mutation({
      query: ({ configType, customerId, payload }) => ({
        url: `${baseUrl}/${configType}`,
        method: 'POST',
        params: { customerId },
        body: payload,
      }),
    }),
    updateTeamsPolicy: builder.mutation({
      query: ({ configType, customerId, identity, payload }) => ({
        url: `${baseUrl}/${configType}`,
        method: 'PUT',
        params: { customerId, identity },
        body: payload,
      }),
    }),
    deleteTeamsPolicy: builder.mutation({
      query: ({ configType, customerId, identity }) => ({
        url: `${baseUrl}/${configType}`,
        method: 'DELETE',
        params: { customerId, identity },
      }),
    }),
  }),
})

export const {
  useGetTeamsPoliciesQuery,
  useLazyGetTeamsPoliciesQuery,
  useGetTeamsPolicyQuery,
  useLazyGetTeamsPolicyQuery,
  useGetTeamsAppCatalogQuery,
  useGetTeamsAppCatalogPagesInfiniteQuery,
  useCreateTeamsPolicyMutation,
  useUpdateTeamsPolicyMutation,
  useDeleteTeamsPolicyMutation,
} = teamsApi

export const useGetCustomerTeamsAppCatalogPages = (queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)
  const { search = '', ...options } = queryOptions

  return useGetTeamsAppCatalogPagesInfiniteQuery(
    { customerId, search },
    {
      refetchOnMountOrArgChange: TEAMS_APP_CATALOG_CACHE_SECONDS,
      ...options,
      skip: Boolean(options.skip) || !customerId,
    },
  )
}

export const useGetCustomerTeamsAppCatalog = (queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)
  const { appIds = [], ...options } = queryOptions

  return useGetTeamsAppCatalogQuery(
    { appIds, customerId },
    {
      refetchOnMountOrArgChange: TEAMS_APP_CATALOG_CACHE_SECONDS,
      ...options,
      skip: Boolean(options.skip) || !customerId,
    },
  )
}

export const useGetCustomerTeamsPolicies = (configType, queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetTeamsPoliciesQuery(
    { configType, customerId },
    {
      ...queryOptions,
      skip: Boolean(queryOptions.skip) || !customerId || !configType,
    },
  )
}

export const useGetCustomerTeamsPolicy = ({ configType, identity }, queryOptions = {}) => {
  const customerId = useSelector(getSelectedCustomerId)

  return useGetTeamsPolicyQuery(
    { configType, customerId, identity },
    {
      ...queryOptions,
      skip: Boolean(queryOptions.skip) || !customerId || !configType || !identity,
    },
  )
}
